import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Loader2, ArrowLeft, Eye, Heart, MessageCircle, Share2,
  ExternalLink, RefreshCw, Users, Calendar as CalendarIcon, Film,
  PoundSterling,
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import {
  format, startOfDay, endOfDay, addDays, differenceInCalendarDays,
} from 'date-fns';
import axios from 'axios';
import SEO from '@/components/SEO';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('kylo_admin_token');
const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

const fmt = (n) => {
  const v = Number(n || 0);
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return `${v}`;
};

const toMillis = (ct) => {
  if (!ct) return 0;
  if (/^\d+$/.test(String(ct))) {
    const n = parseInt(ct, 10);
    return n < 1e12 ? n * 1000 : n; // seconds → ms
  }
  const t = Date.parse(ct);
  return Number.isNaN(t) ? 0 : t;
};

const fmtDate = (ct) => {
  const ms = toMillis(ct);
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const Metric = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-1.5 text-sm">
    <Icon className="w-4 h-4 text-zinc-400 shrink-0" />
    <span className="font-semibold text-white tabular-nums">{fmt(value)}</span>
    <span className="text-zinc-300">{label}</span>
  </div>
);

const CHART_TOOLTIP_STYLE = {
  backgroundColor: '#12121a',
  border: '1px solid #2a2a3a',
  borderRadius: 8,
  color: '#fff',
  fontSize: 12,
};

const ChartCard = ({ title, data, dataKey, color, overlayKey, overlayColor, overlayName, footer }) => (
  <Card className="bg-[#12121a] border-[#1e1e2e] p-4">
    <p className="text-[11px] uppercase tracking-wider text-zinc-300 mb-3">{title}</p>
    <div className="h-48 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fill: '#a1a1aa', fontSize: 11 }}
            stroke="#3f3f46"
            interval="preserveStartEnd"
            minTickGap={24}
          />
          <YAxis
            yAxisId="left"
            tick={{ fill: '#a1a1aa', fontSize: 11 }}
            stroke="#3f3f46"
            tickFormatter={fmt}
            width={44}
            allowDecimals={false}
          />
          {overlayKey && (
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fill: '#f59e0b', fontSize: 11 }}
              stroke="#6b4e16"
              tickFormatter={(v) => `£${fmt(v)}`}
              width={52}
            />
          )}
          <Tooltip
            contentStyle={CHART_TOOLTIP_STYLE}
            labelStyle={{ color: '#e4e4e7' }}
            formatter={(val, name, props) => {
              if (overlayName && name === overlayName) {
                const comm = props?.payload?.commission;
                const commStr = (comm || comm === 0) ? ` · £${Number(comm).toLocaleString('en-GB')} earned` : '';
                return [`£${Number(val).toLocaleString('en-GB')}${commStr}`, overlayName];
              }
              return [Number(val).toLocaleString('en-GB'), title];
            }}
          />
          <Line
            yAxisId="left"
            name={title}
            type="monotone"
            dataKey={dataKey}
            stroke={color}
            strokeWidth={2}
            dot={{ r: 2, fill: color }}
            activeDot={{ r: 4 }}
          />
          {overlayKey && (
            <Line
              yAxisId="right"
              name={overlayName}
              type="monotone"
              dataKey={overlayKey}
              stroke={overlayColor}
              strokeWidth={2}
              dot={{ r: 3, fill: overlayColor }}
              activeDot={{ r: 5 }}
              connectNulls={false}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
    {footer}
  </Card>
);

const AdminCreatorPage = () => {
  const navigate = useNavigate();
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null); // { creator, videos, fetched_at, cached }
  const [dateRange, setDateRange] = useState(undefined); // { from, to }
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [activeDays, setActiveDays] = useState(7);
  const [gmvByDay, setGmvByDay] = useState({}); // { 'yyyy-MM-dd': { gmv, commission } } — prototype, not persisted
  const [gmvOpen, setGmvOpen] = useState(false);
  const [gmvForm, setGmvForm] = useState({ day: '', gmv: '', commission: '' });

  useEffect(() => {
    if (!getToken()) navigate('/backendcontrol');
  }, [navigate]);

  const fetchInsights = useCallback(async (force, days) => {
    if (!url.trim()) { toast.error('Paste a TikTok creator URL or @handle'); return; }
    setLoading(true);
    try {
      if (!force) {
        const c = await axios.get(`${API}/api/admin/creator-insights`, {
          params: { url: url.trim(), days }, headers: authHeaders(),
        });
        if (c.data?.cached) {
          setData({ ...c.data });
          toast.message('Loaded cached result (no Apify charge). Use Refresh for live data.');
          setLoading(false);
          return;
        }
      }
      const r = await axios.post(`${API}/api/admin/creator-insights`, { url: url.trim(), days }, { headers: authHeaders() });
      setData({ ...r.data });
      toast.success('Fetched from TikTok');
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        navigate('/backendcontrol'); return;
      }
      toast.error(err.response?.data?.detail || 'Lookup failed');
    } finally {
      setLoading(false);
    }
  }, [url, navigate]);

  // Preset buttons set the window + range AND trigger the fetch (nothing loads until pressed).
  const runWithRange = useCallback((days) => {
    const today = new Date();
    setActiveDays(days);
    setDateRange({ from: startOfDay(addDays(today, -(days - 1))), to: endOfDay(today) });
    fetchInsights(false, days);
  }, [fetchInsights]);

  // Videos within the selected date range, newest first.
  const videos = useMemo(() => {
    const all = (data?.videos || []).slice();
    if (!dateRange?.from) return all.sort((a, b) => toMillis(b.create_time) - toMillis(a.create_time));
    const from = startOfDay(dateRange.from).getTime();
    const to = endOfDay(dateRange.to || dateRange.from).getTime();
    return all
      .filter((v) => { const t = toMillis(v.create_time); return t >= from && t <= to; })
      .sort((a, b) => toMillis(b.create_time) - toMillis(a.create_time));
  }, [data, dateRange]);

  const totals = useMemo(() => videos.reduce(
    (acc, v) => ({
      views: acc.views + (v.views || 0),
      likes: acc.likes + (v.likes || 0),
      comments: acc.comments + (v.comments || 0),
      shares: acc.shares + (v.shares || 0),
    }),
    { views: 0, likes: 0, comments: 0, shares: 0 },
  ), [videos]);

  // Per-day series for the line charts (gaps filled with zeros).
  const daily = useMemo(() => {
    if (!dateRange?.from) return [];
    const from = startOfDay(dateRange.from);
    const to = endOfDay(dateRange.to || dateRange.from);
    const span = Math.min(differenceInCalendarDays(to, from), 365);

    const buckets = {};
    videos.forEach((v) => {
      const key = format(new Date(toMillis(v.create_time)), 'yyyy-MM-dd');
      if (!buckets[key]) buckets[key] = { videos: 0, views: 0, likes: 0, engagement: 0 };
      buckets[key].videos += 1;
      buckets[key].views += v.views || 0;
      buckets[key].likes += v.likes || 0;
      buckets[key].engagement += (v.likes || 0) + (v.comments || 0) + (v.shares || 0);
    });

    const out = [];
    for (let i = 0; i <= span; i += 1) {
      const d = addDays(from, i);
      const key = format(d, 'yyyy-MM-dd');
      const b = buckets[key] || { videos: 0, views: 0, likes: 0, engagement: 0 };
      out.push({ key, label: format(d, 'd MMM'), ...b });
    }
    return out;
  }, [videos, dateRange]);

  const creator = data?.creator;
  const rangeLabel = dateRange?.from
    ? `${format(dateRange.from, 'd MMM yyyy')} – ${format(dateRange.to || dateRange.from, 'd MMM yyyy')}`
    : 'Select date range';

  // GMV overlay (prototype, in-memory only).
  const dailyWithGmv = useMemo(
    () => daily.map((d) => ({
      ...d,
      gmv: gmvByDay[d.key]?.gmv ?? null,
      commission: gmvByDay[d.key]?.commission ?? null,
    })),
    [daily, gmvByDay],
  );
  const dayLabel = useCallback(
    (key) => daily.find((d) => d.key === key)?.label || key,
    [daily],
  );
  const openGmvForDay = (key) => {
    const existing = gmvByDay[key];
    setGmvForm({
      day: key,
      gmv: existing?.gmv != null ? String(existing.gmv) : '',
      commission: existing?.commission != null ? String(existing.commission) : '',
    });
    setGmvOpen(true);
  };
  const saveGmvEntry = () => {
    if (!gmvForm.day) return;
    const gmv = parseFloat(gmvForm.gmv);
    if (Number.isNaN(gmv)) { toast.error('Enter a GMV total'); return; }
    const commission = gmvForm.commission === '' ? null : parseFloat(gmvForm.commission);
    setGmvByDay((prev) => ({ ...prev, [gmvForm.day]: { gmv, commission } }));
    toast.success(`Logged £${gmv.toLocaleString('en-GB')} for ${dayLabel(gmvForm.day)}`);
    setGmvOpen(false);
  };
  const removeGmvEntry = (key) => {
    setGmvByDay((prev) => { const n = { ...prev }; delete n[key]; return n; });
    setGmvOpen(false);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <SEO title="Creator Insights" description="Admin creator test tool." noindex />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <button
            type="button"
            onClick={() => navigate('/backendcontrol/dashboard')}
            className="inline-flex items-center gap-1.5 text-sm text-zinc-300 hover:text-white"
            data-testid="creator-back"
          >
            <ArrowLeft className="w-4 h-4" /> Admin dashboard
          </button>
          <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-pink-500/15 text-pink-400">Test · Creator</span>
        </div>

        <h1 className="text-2xl font-bold mb-1">Creator insights</h1>
        <p className="text-sm text-zinc-400 mb-6">Enter a TikTok creator's @handle, then pick a range to pull every video posted in that window.</p>

        <Card className="bg-[#12121a] border-[#1e1e2e] p-5 mb-6">
          <Input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="@creator"
            className="bg-[#0a0a0f] border-[#2a2a3a] text-white placeholder:text-zinc-500"
            data-testid="creator-url-input"
          />
          <p className="text-[11px] text-zinc-400 mt-3 mb-2">Pick a date range to fetch (pulls every video in the window, pinned posts excluded):</p>
          <div className="flex flex-wrap gap-2">
            {[7, 14, 30].map((d) => (
              <Button
                key={d}
                onClick={() => runWithRange(d)}
                disabled={loading}
                className="bg-pink-600 hover:bg-pink-500 text-white"
                data-testid={`creator-range-${d}`}
              >
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CalendarIcon className="w-4 h-4 mr-2" />}
                Last {d} days
              </Button>
            ))}
          </div>
        </Card>

        {data && (
          <>
            {/* Creator header + date range */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-3">
                {creator?.avatar ? (
                  <img src={creator.avatar} alt="" className="w-11 h-11 rounded-full object-cover" />
                ) : (
                  <div className="w-11 h-11 rounded-full bg-[#1e1e2e] flex items-center justify-center"><Users className="w-5 h-5 text-zinc-400" /></div>
                )}
                <div>
                  <p className="font-semibold">@{creator?.handle}</p>
                  <p className="text-xs text-zinc-400">
                    {creator?.nickname ? `${creator.nickname} · ` : ''}{creator?.followers != null ? `${fmt(creator.followers)} followers` : ''}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-[#2a2a3a] bg-[#12121a] text-zinc-100 hover:bg-[#1a1a26] hover:text-white"
                      data-testid="creator-date-range"
                    >
                      <CalendarIcon className="w-3.5 h-3.5 mr-1.5" /> {rangeLabel}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="end">
                    <Calendar
                      mode="range"
                      selected={dateRange}
                      onSelect={setDateRange}
                      numberOfMonths={2}
                      defaultMonth={dateRange?.from}
                    />
                  </PopoverContent>
                </Popover>
                <Button variant="outline" size="sm" onClick={() => fetchInsights(true, activeDays)} disabled={loading} className="border-[#2a2a3a] bg-[#12121a] text-zinc-100 hover:bg-[#1a1a26] hover:text-white" data-testid="creator-refresh">
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
                </Button>
              </div>
            </div>

            {data.fetched_at && (
              <p className="text-[11px] text-zinc-500 mb-4" data-testid="creator-fetched-at">
                {data.cached ? 'Cached' : 'Fetched'} {new Date(data.fetched_at).toLocaleString('en-GB')}
              </p>
            )}

            {/* Totals */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6" data-testid="creator-totals">
              {[
                { label: 'Videos posted', value: videos.length, icon: Film, color: 'text-violet-400' },
                { label: 'Total views', value: totals.views, icon: Eye, color: 'text-blue-400' },
                { label: 'Total likes', value: totals.likes, icon: Heart, color: 'text-pink-400' },
                { label: 'Total comments', value: totals.comments, icon: MessageCircle, color: 'text-amber-400' },
                { label: 'Total shares', value: totals.shares, icon: Share2, color: 'text-emerald-400' },
              ].map((t) => (
                <Card key={t.label} className="bg-[#12121a] border-[#1e1e2e] p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <t.icon className={`w-4 h-4 ${t.color}`} />
                    <p className="text-[11px] uppercase tracking-wider text-zinc-300">{t.label}</p>
                  </div>
                  <p className="text-2xl font-bold text-white">{fmt(t.value)}</p>
                </Card>
              ))}
            </div>

            {/* Charts */}
            {daily.length > 0 && (
              <div className="grid grid-cols-1 gap-3 mb-6" data-testid="creator-charts">
                <ChartCard
                  title="Videos posted per day"
                  data={dailyWithGmv}
                  dataKey="videos"
                  color="#a78bfa"
                  overlayKey="gmv"
                  overlayColor="#f59e0b"
                  overlayName="GMV"
                  footer={(
                    <div className="flex items-center justify-between mt-2" style={{ paddingLeft: 32, paddingRight: 60 }} data-testid="gmv-day-buttons">
                      {daily.map((d) => {
                        const done = gmvByDay[d.key] != null;
                        return (
                          <button
                            key={d.key}
                            type="button"
                            onClick={() => openGmvForDay(d.key)}
                            title={`Record GMV for ${d.label}`}
                            className={'inline-flex items-center justify-center w-5 h-5 rounded-md border transition-colors ' + (done ? 'bg-amber-400/20 border-amber-400 text-amber-300' : 'border-[#2a2a3a] text-zinc-500 hover:text-amber-400 hover:border-amber-400/60')}
                            data-testid={`gmv-day-btn-${d.key}`}
                          >
                            <PoundSterling className="w-3 h-3" />
                          </button>
                        );
                      })}
                    </div>
                  )}
                />
                <ChartCard title="Views per day" data={daily} dataKey="views" color="#60a5fa" />
              </div>
            )}

            {/* GMV logger dialog (prototype — in-memory only) */}
            <Dialog open={gmvOpen} onOpenChange={setGmvOpen}>
              <DialogContent className="bg-[#12121a] border-[#1e1e2e] text-white sm:max-w-md" data-testid="gmv-dialog">
                <DialogHeader>
                  <DialogTitle>Record GMV — {dayLabel(gmvForm.day)}</DialogTitle>
                  <DialogDescription className="text-zinc-400">
                    Enter this day's GMV total and the commission you earned (both actual £ amounts). Prototype only, nothing is saved.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-zinc-300">GMV total (£)</Label>
                      <Input
                        type="number" min="0" value={gmvForm.gmv}
                        onChange={(e) => setGmvForm((f) => ({ ...f, gmv: e.target.value }))}
                        placeholder="0"
                        className="bg-[#0a0a0f] border-[#2a2a3a] text-white mt-1"
                        data-testid="gmv-amount-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-zinc-300">Commission earned (£)</Label>
                      <Input
                        type="number" min="0" value={gmvForm.commission}
                        onChange={(e) => setGmvForm((f) => ({ ...f, commission: e.target.value }))}
                        placeholder="0"
                        className="bg-[#0a0a0f] border-[#2a2a3a] text-white mt-1"
                        data-testid="gmv-commission-input"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {gmvByDay[gmvForm.day] != null && (
                      <Button variant="outline" onClick={() => removeGmvEntry(gmvForm.day)} className="border-[#2a2a3a] bg-transparent text-zinc-200 hover:bg-[#1a1a26]" data-testid="gmv-remove-btn">
                        Remove
                      </Button>
                    )}
                    <Button onClick={saveGmvEntry} className="flex-1 bg-amber-500 hover:bg-amber-400 text-black font-semibold" data-testid="gmv-save-btn">
                      Save
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Video grid */}
            {videos.length === 0 ? (
              <p className="text-sm text-zinc-400" data-testid="creator-no-videos">No videos in this range.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="creator-videos">
                {videos.map((v, i) => (
                  <Card key={v.video_id || i} className="bg-[#12121a] border-[#1e1e2e] overflow-hidden" data-testid={`creator-video-${i}`}>
                    <a href={v.url || '#'} target="_blank" rel="noopener noreferrer" className="block relative group">
                      {v.cover ? (
                        <img src={v.cover} alt="" className="w-full aspect-[9/16] object-cover" />
                      ) : (
                        <div className="w-full aspect-[9/16] bg-[#1e1e2e]" />
                      )}
                      <span className="absolute top-1.5 right-1.5 bg-black/70 rounded px-1.5 py-0.5 text-[10px] inline-flex items-center gap-1">
                        <ExternalLink className="w-2.5 h-2.5" />
                      </span>
                    </a>
                    <div className="p-2.5 space-y-1.5">
                      <p className="text-[11px] font-medium text-zinc-200">{fmtDate(v.create_time)}</p>
                      <div className="space-y-1">
                        <Metric icon={Eye} label="views" value={v.views} />
                        <Metric icon={Heart} label="likes" value={v.likes} />
                        <Metric icon={MessageCircle} label="comments" value={v.comments} />
                        <Metric icon={Share2} label="shares" value={v.shares} />
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminCreatorPage;
